import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useCurrentWidth } from 'effects';
import type { INavMenuItem } from './DesktopNavMenu';

import { BuildingSkyscraperIcon } from 'ui/Icons/components/BuildingSkyscraper.component';
import { InfoSquareIcon } from 'ui/Icons/components/InfoSquare.component';
import { BedFilledIcon } from 'ui/Icons/components/BedFilled.component';
import { SlideshowIcon } from 'ui/Icons/components/Slideshow.component';
import { StarHalfFilledIcon } from 'ui/Icons/components/StarHalfFilled.component';
import { FolderIcon } from 'ui/Icons/components/Folder.component';
import { ListIcon } from 'ui/Icons/components/List.component';
import { MapPinFilledIcon } from 'ui/Icons/components/MapPinFilled.component';
import { UserIcon } from 'ui/Icons/components/user.component';
import { DotsHorizontalIcon } from 'ui/Icons/components/DotsHorizontal.component';
import { IIconProps } from 'ui/CircleIconButton';
import { theme } from '../../../../tailwind.config';

type IconComponent = React.FC<IIconProps>;

type TabId =
  | 'details'
  | 'announcements'
  | 'rooms'
  | 'gallery'
  | 'reviews'
  | 'documents'
  | 'policies'
  | 'location'
  | 'contacts';

interface IBottomTabsProps {
  items: INavMenuItem[];
  activeSection?: string;
  onScrollToSection: (sectionId: string) => void;
  setActiveSection: (sectionId: string) => void;
}

const TAB_ORDER: TabId[] = [
  'details',
  'announcements',
  'rooms',
  'gallery',
  'reviews',
  'documents',
  'policies',
  'location',
  'contacts',
];

const TAB_LABELS: Record<TabId, string> = {
  details: 'Details',
  announcements: 'Announcements',
  rooms: 'Rooms',
  gallery: 'Gallery',
  reviews: 'Reviews',
  documents: 'Documents',
  policies: 'Policies',
  location: 'Location',
  contacts: 'Contacts',
};

const TAB_ICONS: Record<TabId, IconComponent> = {
  details: BuildingSkyscraperIcon,
  announcements: InfoSquareIcon,
  rooms: BedFilledIcon,
  gallery: SlideshowIcon,
  reviews: StarHalfFilledIcon,
  documents: FolderIcon,
  policies: ListIcon,
  location: MapPinFilledIcon,
  contacts: UserIcon,
};

const TAB_MIN_WIDTH_PX = 76; // tuned to match Figma density and truncation
const MORE_TAB_WIDTH_PX = 56;
const BAR_HEIGHT_PX = 70;

export const BottomTabs: React.FC<IBottomTabsProps> = ({ items, activeSection, onScrollToSection, setActiveSection }) => {
  const { isTabletOrMobile } = useCurrentWidth();
  const barRef = useRef<HTMLDivElement>(null);
  const overflowRef = useRef<HTMLDivElement>(null);

  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [isOverflowOpen, setIsOverflowOpen] = useState(false);

  const visibleIds = useMemo(() => {
    const visible = new Set(items.filter(i => i.isVisible !== false).map(i => i.id));
    return TAB_ORDER.filter(id => visible.has(id));
  }, [items]);

  const { shownIds, hiddenIds } = useMemo(() => {
    const total = visibleIds.length;
    if (!containerWidth || total === 0) {
      return { shownIds: visibleIds, hiddenIds: [] as TabId[] };
    }
    // barRef measures the padded container; child layout uses content box (minus 20px left + 20px right padding)
    const effectiveWidth = Math.max(0, containerWidth - 40);

    // Compute how many can fit. If not all fit, reserve last slot for "more".
    const rawSlots = Math.floor(effectiveWidth / TAB_MIN_WIDTH_PX);
    const canFitAll = rawSlots >= total;
    if (canFitAll) {
      return { shownIds: visibleIds, hiddenIds: [] as TabId[] };
    }

    // Reserve space for the "more" button. Ensure at least 1 visible tab.
    const availableForTabs = Math.max(1, Math.floor((effectiveWidth - MORE_TAB_WIDTH_PX) / TAB_MIN_WIDTH_PX));
    const shown = visibleIds.slice(0, availableForTabs);
    const hidden = visibleIds.slice(availableForTabs);
    return { shownIds: shown, hiddenIds: hidden };
  }, [visibleIds, containerWidth]);

  // Measure bar width (ResizeObserver when available)
  useLayoutEffect(() => {
    if (!isTabletOrMobile) return;
    const el = barRef.current;
    if (!el) return;

    const update = () => setContainerWidth(el.getBoundingClientRect().width);
    update();

    if (typeof ResizeObserver === 'undefined') {
      const onResize = () => update();
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }

    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, [isTabletOrMobile]);

  useEffect(() => {
    if (!isOverflowOpen) return;
    const onDown = (e: MouseEvent) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target as Node)) {
        setIsOverflowOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isOverflowOpen]);

  // If we just resized and there is no overflow anymore, close the menu
  useEffect(() => {
    if (hiddenIds.length === 0 && isOverflowOpen) {
      setIsOverflowOpen(false);
    }
  }, [hiddenIds.length, isOverflowOpen]);

  if (!isTabletOrMobile) return null;

  const handleTabClick = (id: TabId) => {
    setIsOverflowOpen(false);
    onScrollToSection(id);
    setActiveSection(id);
  };

  const renderTab = (id: TabId, opts?: { hideLabel?: boolean }) => {
    const isActive = activeSection === id;
    const Icon = TAB_ICONS[id];

    return (
      <button
        key={id}
        type="button"
        onClick={() => handleTabClick(id)}
        className={`flex flex-col items-center justify-center gap-[5px] h-full px-[5px] select-none ${
          isActive ? 'bg-teal-20 border-t-[3px] border-green-hard' : 'bg-gray-10 border-t-[3px] border-transparent'
        }`}
        style={{ minWidth: TAB_MIN_WIDTH_PX }}
      >
        <div className="w-[24px] h-[24px]">
          <Icon fill={isActive ? theme.colors['green-hard'] : theme.colors.black} />
        </div>
        {!opts?.hideLabel && (
          <span
            className={`font-pt-sans text-[13px] leading-[16px] max-w-[86px] truncate ${
              isActive ? 'font-bold text-green-hard' : 'font-normal text-black'
            }`}
            title={TAB_LABELS[id]}
          >
            {TAB_LABELS[id]}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      <div style={{ height: BAR_HEIGHT_PX }} className="lg:hidden" />

      <div
        className="fixed bottom-0 left-0 right-0 z-30 bg-gray-10 lg:hidden w-[100vw]"
        style={{ height: BAR_HEIGHT_PX }}
      >
        <div ref={barRef} className="relative h-full px-[20px] flex items-center justify-between">
          {/* Tabs row */}
          <div className="bottom-tabs flex h-full flex-1 justify-between items-stretch overflow-hidden">
            {shownIds.map(id => renderTab(id))}
          </div>

          {/* Overflow */}
          {hiddenIds.length > 0 && (
            <div ref={overflowRef} className="relative h-full flex items-stretch cursor-pointer">
              {/*
                If the active section is in the overflow list, highlight the "more" button as active
                so users still get an indicator of where they are.
              */}
              {(() => {
                const isOverflowActive = !!activeSection && hiddenIds.includes(activeSection as TabId);
                return (
                  <button
                    type="button"
                    onClick={() => setIsOverflowOpen(v => !v)}
                    className={`flex flex-col items-center justify-center gap-[5px] h-full px-[5px] ${
                      isOverflowActive ? 'bg-teal-20 border-t-[3px] border-green-hard' : 'bg-gray-10 border-t-[3px] border-transparent'
                    }`}
                    style={{ width: MORE_TAB_WIDTH_PX }}
                    aria-haspopup="menu"
                    aria-expanded={isOverflowOpen}
                  >
                    <div className="w-[24px] h-[24px]">
                      <DotsHorizontalIcon fill={isOverflowActive ? theme.colors['green-hard'] : theme.colors.black} />
                    </div>
                  </button>
                );
              })()}

              {isOverflowOpen && (
                <div className="absolute bottom-full right-0 mb-[10px] z-40 border border-solid border-gray-20 rounded-[8px] shadow-lg min-w-[150px] overflow-hidden">
                  <div className="flex flex-col py-[20px] bg-gray-10">
                    {hiddenIds.map(id => {
                      const isActive = activeSection === id;
                      return (
                        <button
                          key={`overflow-${id}`}
                          type="button"
                          onClick={() => handleTabClick(id)}
                          className={`flex items-center gap-[10px] px-[20px] text-left ${
                            isActive ? 'font-bold text-green-hard bg-teal-20' : 'font-normal text-black bg-transparent '
                          }`}
                        >
                          <span className="font-pt-sans text-[15px] leading-[34px] ">{TAB_LABELS[id]}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

